import { Col, Radio, Select, Space, Table, Tabs, message, theme, Modal, DatePicker } from 'antd'
import React, { useEffect, useState } from 'react'
import Templates from '../../utils/templates'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { generateAgoraRtcToken, getBookings, updateBooking } from '../../utils/utils'
import { generateUniqueUIDFromString, handleError } from '../../../lib/utils'
import coreReducer from '../../../../core/utils/reducer'
import DiagnosisOverview from '../../../Medical History/components/DiagnosisOverview'
import { handleCancelBooking } from '../../../lib/booking-utils'
import BookingSummaryModal from '../BookingSummary'
import CoreEnums from '../../../../core/utils/enums'
import CustomRow from '../../../reusable-components/CustomRow'
import BookingShortcut from '../../../reusable-components/BookingShortcut'
import { deviceDetect } from 'react-device-detect'
import adminBookingsReducer from '../../../Admin/bookings/utils/reducer'
import CustomButton from '../../../reusable-components/CustomButton'
import { faRefresh } from '@fortawesome/free-solid-svg-icons'
import { generateTimeSlots } from '../../../Appointments-Calendar/full-calendar-components/Viewing Record/utils'

const PatientBookingsView = ({ userRef }) => {
  const state = useSelector((state) => state)
  const dependantState = useSelector((state) => state.dependents.data)
  const adminBookingsState = useSelector((state) => state.adminBookings.data)
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [booking, setBooking] = useState(null)
  const [bookingOpen, setBookingOpen] = useState()
  const [currentUser, setCurrentUser] = useState(state.auth.agiliteUser._id)
  const [filterValue, setFilterValue] = useState('all')
  const [dependentData, setDependentData] = useState([])

  // Agora
  const [joined, setJoined] = useState(false)
  const [agoraToken, setAgoraToken] = useState(null)
  const [agoraChannel, setAgoraChannel] = useState(null)
  const [agoraUid, setAgoraUid] = useState(null)

  // Reschedule state
  const [rescheduling, setRescheduling] = useState(false)
  const [rescheduleDate, setRescheduleDate] = useState(null)
  const [rescheduleTime, setRescheduleTime] = useState(null)
  const [rescheduleBooking, setRescheduleBooking] = useState(null)

  const { token } = theme.useToken()

  useEffect(() => {
    handleGetData()
    // eslint-disable-next-line
  }, [
    state.clinics.data,
    state.services.data,
    state.virtualServices.data,
    filterValue,
    state.bookings.data,
    currentUser
  ])

  const handleGetData = () => {
    if (state.auth.agiliteUser) {
      handleGetBookings()
    }
  }

  const handleGetBookings = async () => {
    setLoading(true)

    try {
      const qry = getQuery(filterValue)
      const tmpBookings = await getBookings(state, qry, userRef || currentUser)
      const sortedBookings = sortBookings(tmpBookings)
      if (userRef) {
        setDependentData(sortedBookings)
      } else {
        dispatch(adminBookingsReducer.actions.setRecords(sortedBookings))
      }
    } catch (e) {
      message.error(handleError(e, true))
    }

    setLoading(false)
  }

  const getQuery = (filterValue) => {
    const today = dayjs(new Date()).format('YYYY-MM-DDT00:00:00.000Z')
    const tomorrow = dayjs(new Date()).add(1, 'day').format('YYYY-MM-DDT00:00:00.000Z')
    const upcoming = dayjs(new Date()).add(1, 'day').format('YYYY-MM-DD')
    const past = dayjs(new Date()).format('YYYY-MM-DDT00:00:00.000Z')

    switch (filterValue) {
      case 'all':
        return {}
      case 'today':
        return {
          bookingDate: {
            $gt: today,
            $lt: tomorrow
          }
        }
      case 'upcoming':
        return {
          bookingDate: {
            $gte: upcoming
          }
        }
      case 'past':
        return {
          bookingDate: {
            $lt: past
          }
        }
      default:
        return {}
    }
  }

  const sortBookings = (bookings) => {
    // Handle null/undefined bookings array
    if (!bookings || !Array.isArray(bookings)) {
      return []
    }

    return bookings.sort((a, b) => {
      // Handle null/undefined startTime values
      const aStartTime = a.startTime || '00:00'
      const bStartTime = b.startTime || '00:00'

      const aDate = new Date(
        dayjs(a.bookingDate).set('hours', aStartTime.slice(0, 2)).set('minutes', aStartTime.slice(-2))
      )
      const bDate = new Date(
        dayjs(b.bookingDate).set('hours', bStartTime.slice(0, 2)).set('minutes', bStartTime.slice(-2))
      )

      return bDate - aDate
    })
  }

  const handleOpenBooking = (record) => {
    if (record.status !== CoreEnums.bookingStatuses.completed) {
      setBookingOpen(true)
      setBooking(record)
    } else {
      dispatch(
        coreReducer.actions.addTab({
          key: `${CoreEnums.tabKeys.MY_BOOKINGS}_${record._id}`,
          closable: true,
          // TODO: Why is this label test?
          label: 'test',
          children: <DiagnosisOverview data={record} />
        })
      )
    }
  }

  const joinVirtualConsult = async (record) => {
    let agoraCreds = null

    try {
      agoraCreds = await generateAgoraRtcToken(record._id, generateUniqueUIDFromString(state.auth.agiliteUser._id))
      setAgoraToken(agoraCreds.token)
      setAgoraChannel(agoraCreds.channelName)
      setAgoraUid(agoraCreds.uid)
      setJoined(true)

      setBooking(record)
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  const handleReschedule = (record) => {
    setRescheduleBooking(record)
    setRescheduling(true)
  }

  const confirmReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime) {
      message.error('Please select both date and time')
      return
    }

    // Double-check appointment rules for patients
    if (state.auth.agiliteUser?.extraData?.role?.type === 'patient') {
      // Parse date and time more robustly
      const appointmentDate = dayjs(rescheduleBooking.bookingDate)
      const [hours, minutes] = rescheduleBooking.startTime.split(':')
      const appointmentDateTime = appointmentDate.hour(parseInt(hours)).minute(parseInt(minutes)).second(0)

      const currentTime = dayjs()
      const hoursUntilAppointment = appointmentDateTime.diff(currentTime, 'hours', true)
      const appointmentInPast = appointmentDateTime.isBefore(currentTime)

      if (appointmentInPast) {
        message.error('Cannot reschedule - appointment has already passed.')
        return
      }

      if (hoursUntilAppointment < 1) {
        message.error(
          `Cannot reschedule - appointment is in ${hoursUntilAppointment.toFixed(
            1
          )} hours. Must be 1+ hours in advance.`
        )
        return
      }
    }

    setLoading(true)
    try {
      // Find service info for calculating end time
      const serviceInfo = state.services.data.find((i) => i._id === rescheduleBooking.service)
      if (!serviceInfo) {
        message.error('Service information not found')
        return
      }

      const newBooking = await updateBooking(
        {
          bookingDate: dayjs(rescheduleDate).format('YYYY-MM-DD'),
          startTime: rescheduleTime,
          endTime: dayjs(rescheduleTime, 'HH:mm').add(serviceInfo.timeslotInterval, 'minutes').format('HH:mm')
        },
        { _id: rescheduleBooking._id }
      )

      // Update the booking state
      if (userRef) {
        const updatedData = dependentData.map((booking) =>
          booking._id === rescheduleBooking._id ? { ...booking, ...newBooking } : booking
        )
        setDependentData(updatedData)
      } else {
        const updatedBookings = adminBookingsState.map((booking) =>
          booking._id === rescheduleBooking._id ? { ...booking, ...newBooking } : booking
        )
        dispatch(adminBookingsReducer.actions.setRecords(updatedBookings))
      }

      message.success('Appointment rescheduled successfully')
      resetRescheduleData()
    } catch (e) {
      message.error(handleError(e, true))
    }
    setLoading(false)
  }

  const resetRescheduleData = () => {
    setRescheduleDate(null)
    setRescheduleTime(null)
    setRescheduling(false)
    setRescheduleBooking(null)
  }

  const bookingFilterOptions = [
    {
      value: 'past',
      label: 'Previous'
    },
    {
      value: 'today',
      label: 'Today'
    },
    {
      value: 'upcoming',
      label: 'Upcoming'
    },
    {
      value: 'all',
      label: 'All'
    }
  ]

  return (
    <>
      <CustomRow justify='center'>
        {userRef ? undefined : (
          <Col span={24}>
            <BookingShortcut showSOS={false} />
          </Col>
        )}
        <Col span={24}>
          <CustomRow className='basic-card'>
            <Col span={24}>
              <Space wrap>
                {deviceDetect().isMobile ? undefined : (
                  <CustomButton
                    text='Refresh'
                    size='small'
                    icon={faRefresh}
                    onClick={handleGetBookings}
                    type='primary'
                    disabled={loading}
                  />
                )}
                {deviceDetect().isMobile ? (
                  <Select
                    style={{ width: 350, maxWidth: '100%' }}
                    onChange={(value) => {
                      setFilterValue(value)
                    }}
                    value={filterValue}
                    options={bookingFilterOptions}
                  />
                ) : (
                  <Radio.Group
                    disabled={loading}
                    style={{
                      width: '100%'
                    }}
                    onChange={(e) => {
                      setFilterValue(e.target.value)
                    }}
                    value={filterValue}
                  >
                    {bookingFilterOptions.map((option) => (
                      <Radio.Button key={option.value} value={option.value}>
                        {option.label}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                )}
              </Space>
            </Col>
            {dependantState.length > 0 ? (
              <Col span={24}>
                {deviceDetect().isMobile ? undefined : (
                  <Tabs
                    onChange={(key) => {
                      setCurrentUser(key)
                    }}
                  >
                    <Tabs.TabPane tab='Myself' key={state.auth.agiliteUser._id}></Tabs.TabPane>
                    {dependantState.map((dependant) => {
                      return <Tabs.TabPane tab={dependant.firstName} key={dependant._id}></Tabs.TabPane>
                    })}
                  </Tabs>
                )}
              </Col>
            ) : undefined}
            <Col span={24}>
              <Table
                showHeader={deviceDetect().isMobile ? false : true}
                dataSource={userRef ? dependentData : adminBookingsState}
                columns={Templates.columnTemplate(
                  token,
                  (record) => {
                    handleCancelBooking(state.auth, record, updateBooking, handleGetData, token)
                  },
                  null,
                  null,
                  null,
                  null,
                  (record) => {
                    joinVirtualConsult(record)
                  },
                  handleReschedule
                ).filter((col) => !col.hidden)}
                onRow={(record) => {
                  return {
                    onClick: () => {
                      handleOpenBooking(record)
                    }
                  }
                }}
                rowKey={(record) => record._id}
                pagination={false}
                loading={loading}
                bordered={false}
                scroll={deviceDetect().isMobile ? {} : { x: 1000 }}
              />
            </Col>
          </CustomRow>
        </Col>
      </CustomRow>
      <BookingSummaryModal
        joinVirtualConsult={joinVirtualConsult}
        data={booking}
        setData={setBooking}
        modalOpen={bookingOpen}
        setModalOpen={setBookingOpen}
        joined={joined}
        setJoined={setJoined}
        agoraToken={agoraToken}
        agoraChannel={agoraChannel}
        agoraUid={agoraUid}
      />

      {/* Reschedule Modal */}
      <Modal
        title='Reschedule Appointment'
        open={rescheduling}
        onOk={confirmReschedule}
        onCancel={resetRescheduleData}
        okText='Confirm Reschedule'
        cancelText='Cancel'
        confirmLoading={loading}
        width={600}
      >
        {rescheduleBooking && (
          <>
            <div style={{ marginBottom: 16 }}>
              <strong>Current Appointment:</strong>
              <br />
              {dayjs(rescheduleBooking.bookingDate).format('MMMM DD, YYYY')} at {rescheduleBooking.startTime}
            </div>

            <Space direction='vertical' style={{ width: '100%' }} size='large'>
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>New Date:</label>
                <DatePicker
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                  style={{ width: '100%' }}
                  onChange={setRescheduleDate}
                  value={rescheduleDate}
                  size='large'
                  placeholder='Choose new date'
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>New Time:</label>
                <Select
                  disabled={!rescheduleDate}
                  placeholder='Choose new time'
                  style={{ width: '100%' }}
                  onChange={setRescheduleTime}
                  value={rescheduleTime}
                  size='large'
                  options={generateTimeSlots().map((timeslot) => ({
                    value: timeslot,
                    label: timeslot
                  }))}
                />
              </div>
            </Space>
          </>
        )}
      </Modal>
    </>
  )
}

export default PatientBookingsView
